import { useConfigStore } from '@/stores/ConfigStore'
import { useCardStore } from '@/stores/CardStore'
import { getFromLocalStorageByUserId } from '@/helpers/general'
import { extractBaseParams, buildSearchParams } from '@/helpers/filters'
import {
  FILTERS_BLOCK_1,
  CATEGORIES_FILTER_ITEM,
  BATCH_FILTER_ITEM,
  BRAND_FILTER_ITEM,
  ATTRIBUTE_FILTER_ITEM,
  GRADED_FILTER_ITEM,
  GRADE_FILTER_ITEM,
  INVENTORY_FILTERS_OPTIONS,
  YEAR_FILTER_ITEM,
  BOX_FILTER_ITEM,
  ROW_FILTER_ITEM,
  SHELF_FILTER_ITEM,
  CABINET_FILTER_ITEM,
  TEAM_FILTER_ITEM,
  SET_FILTER_ITEM,
  LOCATION_FILTER_ITEM,
  CUSTOM_FIELDS_FILTER_ITEM,
  QUANTITY_FILTER_ITEM,
  RESTOCKABLE_FILTER_ITEM,
  BASE_PARAM_KEYS
 } from '@/constants'

export const getInventoryStatus = (card) => {
  if (card.status === 'draft') {
    return 'draft'
  }
  
  if (card.status !== 'draft' && card.quantity <= 0 && card.restockable) {
    return 'out_of_stock'
  }

  if (card.status !== 'draft' && card.quantity <= 0 && !card.restockable) {
    return 'sold_out'
  }

  if (card.status !== 'draft' && card.quantity) {
    return 'available'
  }
}

const getTemplate_ = ({ templateField, templateId }) => {
  const configStore = useConfigStore()
  const cardStore = useCardStore()

  let template 
  for(let prop in configStore.config[templateField]) {
    if (configStore.config[templateField][prop].id === cardStore.selectedCardData[templateId]) {
      template = configStore.config[templateField][prop]
    }
  }

  return template?.name || '{Template Name}'
}

export const getTemplateName = ({ mainField, templateField, templateId, template, selectedTemplate}) => { 
  const cardStore = useCardStore()
  if (selectedTemplate) {
    return selectedTemplate.name
  }

  if (!cardStore.selectedCardData[mainField] && !cardStore.selectedCardData[templateId] && cardStore.selectedCardData[template]) {
    return 'Default CDP Template'
  }
  if (cardStore.selectedCardData[mainField] && !cardStore.selectedCardData[templateId]) {
    return `Custom ${mainField === 'description' ? 'Description' : 'Title'}`
  }

  if (cardStore.selectedCardData[templateId]) {
    return getTemplate_({ templateField, templateId })
  }

  if (!cardStore.selectedCardData[mainField] && !cardStore.selectedCardData[templateId] && !cardStore.selectedCardData[template]) {
    return '{Template Name}'
  }
}

export const getAvailableFilters = () => {
  const configStore = useConfigStore()
  const cardStore = useCardStore()

  let brandPresent = false
  let attributesPresent = false
  let yearPresent = false
  let boxPresent = false
  let rowPresent = false
  let shelfPresent = false
  let cabinetPresent = false
  let teamPresent = false
  let setPresent = false
  const customFieldsPresent = configStore.config?.customFields?.length > 0

  const availableFilters = [
    CATEGORIES_FILTER_ITEM
  ]

  for(const item of cardStore.cards) {
    if (item.attributes && !attributesPresent) {
      availableFilters.push(ATTRIBUTE_FILTER_ITEM)
      attributesPresent = true
    }
    
    if (item.year && !yearPresent) {
      availableFilters.push(YEAR_FILTER_ITEM)
      yearPresent = true
    }

    if (item.set && !setPresent) {
      availableFilters.push(SET_FILTER_ITEM)
      setPresent = true
    }

    if (item.brand && !brandPresent) {
      availableFilters.push(BRAND_FILTER_ITEM)
      brandPresent = true
    }

    if (item.team && !teamPresent) {
      availableFilters.push(TEAM_FILTER_ITEM)
      teamPresent = true
    }

    if (item.box && !boxPresent) {
      boxPresent = true
    }

    if (item.row && !rowPresent) {
      rowPresent = true
    }

    if (item.shelf && !shelfPresent) {
      shelfPresent = true
    }

    if (item.cabinet && !cabinetPresent) {
      cabinetPresent = true
    }
  }

  availableFilters.push(QUANTITY_FILTER_ITEM)

  // Location filter with only the sub-filters that are present (Box, Row, Shelf, Cabinet)
  const locationChildren = []
  
  if (boxPresent) locationChildren.push(BOX_FILTER_ITEM)
  if (rowPresent) locationChildren.push(ROW_FILTER_ITEM)
  if (shelfPresent) locationChildren.push(SHELF_FILTER_ITEM)
  if (cabinetPresent) locationChildren.push(CABINET_FILTER_ITEM)
  if (locationChildren.length > 0) {
    availableFilters.push({
      ...LOCATION_FILTER_ITEM,
      children: locationChildren
    })
  }

  // CDP-2435 — Batch filter: only surface when the backend reports batches with ≥1 item.
  // unshift → it lands first in `availableFilters`, i.e. directly after Price (the last
  // item of FILTERS_BLOCK_1) in the concatenated menu list.
  const hasBatches = cardStore.availableFilters?.batch?.length > 0
  if (hasBatches) {
    availableFilters.unshift(BATCH_FILTER_ITEM)
  }

  const hasGradedCards = cardStore.availableFilters?.graded?.some(item => (item.value === true || item.value === 1) && item.count > 0)
  if (hasGradedCards) {
    availableFilters.push(GRADED_FILTER_ITEM)
    availableFilters.push(GRADE_FILTER_ITEM)
  }

  if (customFieldsPresent) {
    availableFilters.push(CUSTOM_FIELDS_FILTER_ITEM)
  }

  availableFilters.push(RESTOCKABLE_FILTER_ITEM)


  return {
    status_tabs: {
      selected: 'All',
      options: INVENTORY_FILTERS_OPTIONS,
    },
    filter_blocks: [
      [...FILTERS_BLOCK_1, ...availableFilters],
    ]
  }
}

/**
 * Check if duplication popup is dismissed with 24-hour expiration
 * @param {number} userId - The user ID
 * @returns {boolean} - True if popup should be shown, false if dismissed and not expired
 */
export const checkDismissedDuplication = (userId) => {
  const storageKey = 'duplicationPopupDismissed'
  const dismissedData = sessionStorage.getItem(storageKey)
  
  if (!dismissedData) {
    return true // Show popup if no dismissed data exists
  }
  
  try {
    const parsed = JSON.parse(dismissedData)
    const now = Date.now()
    const twentyFourHours = 24 * 60 * 60 * 1000 // 24 hours in milliseconds
    
    // Check if user has dismissed data
    if (!parsed[userId]) {
      return true // Show popup if user has no dismissed data
    }
    
    const userData = parsed[userId]
    
    // Check if 24 hours have passed
    if (now - userData.timestamp > twentyFourHours) {
      // Remove expired entry for this user
      delete parsed[userId]
      sessionStorage.setItem(storageKey, JSON.stringify(parsed))
      return true // Show popup after expiration
    } else {
      return !userData.dismissed // Don't show if dismissed and not expired
    }
  } catch (error) {
    // Handle legacy format (just 'true' string or old format)
    if (typeof dismissedData === 'string' && dismissedData === 'true') {
      // Convert legacy format to new format
      const newData = {
        [userId]: {
          dismissed: true,
          timestamp: Date.now()
        }
      }
      sessionStorage.setItem(storageKey, JSON.stringify(newData))
      return false // Don't show for legacy dismissed state
    }
    
    return true // Show popup if data is corrupted
  }
}

/**
 * Set duplication popup as dismissed with 24-hour expiration
 * @param {number} userId - The user ID
 */
export const setDismissedDuplication = (userId) => {
  const storageKey = 'duplicationPopupDismissed'
  const existingData = sessionStorage.getItem(storageKey)
  
  let dismissedData = {}
  
  if (existingData) {
    try {
      dismissedData = JSON.parse(existingData)
    } catch (error) {
      // If corrupted, start fresh
      dismissedData = {}
    }
  }
  
  // Add or update user's dismissal data
  dismissedData[userId] = {
    dismissed: true,
    timestamp: Date.now()
  }
  
  sessionStorage.setItem(storageKey, JSON.stringify(dismissedData))
}

/**
 * Get length skipped items where we have isHeader
 * Was prepared to show grouped duplicates - gray full width row in inventory list
 * @param {number} userId - The user ID
 */
export const getInventoryCardLengthWithoutDuplicatesHeader = (card) => {
  const cardStore = useCardStore()
  
  return cardStore.duplicateLength
  // const cardStore = useCardStore()
  // const cardLength = cardStore.cards.length
  // const duplicatesHeaderLength = cardStore.cards.filter(card => card.isHeader).length
  // return cardLength - duplicatesHeaderLength
}

export const getIsDisabled = (status, isErrors) => {
  const cardStore = useCardStore()
  // Sold out/out of stock statuses can't change to sold out/out of stock/available
  return (['sold_out', 'out_of_stock'].includes(cardStore.selectedCardData.inventoryStatus) && 
    ['sold_out', 'out_of_stock', 'available'].includes(status)) ||
    
    // Available status can't change to sold out/out of stock
    (['available'].includes(cardStore.selectedCardData.inventoryStatus) && 
     ['sold_out', 'out_of_stock'].includes(status)) ||
    
    // Draft status can't change to sold out/out of stock
    (['draft'].includes(cardStore.selectedCardData.inventoryStatus) && 
     ['sold_out', 'out_of_stock'].includes(status)) ||
    
    // Available status with errors can't be selected
    (['available'].includes(status) && 
     (isErrors.collx || isErrors.ebay || isErrors.whatnot || isErrors.shopify))
}

export const getStatusItemClasses = (active, status, isErrors) => {
  const baseClasses = [
    active ? 'bg-tint-1' : 'text-gray-900',
    'cursor-pointer select-none relative py-2 pl-3 pr-9'
  ]

  // Check if status should be disabled
  const isDisabled = getIsDisabled(status, isErrors)
    

  if (isDisabled) {
    baseClasses.push('bg-gray-100 pointer-events-none cursor-default')
  }

  return baseClasses
}

/**
 * Build the query string shared by the inventory list + market-prices endpoints.
 * Pure: takes the current filters/page/user rather than reaching into stores, so
 * `fetchInventory` and `fetchMarketPrices` (InventoryStore) can both reuse it.
 *
 * @param {Object}  args
 * @param {Object}  args.itemFilters - cardStore.itemFilters
 * @param {number}  args.page        - cardStore.page
 * @param {number|string} args.userId - userStore.user.id (for perPage/sort localStorage)
 * @param {number}  [args.perPage=25]
 * @returns {{ query: string, perPage_: number }}
 */
export const buildInventoryQuery = ({ itemFilters = {}, page = 1, userId, perPage = 25 }) => {
  const perPage_ = getFromLocalStorageByUserId('perPage_inventory', userId) || perPage

  let sortBy_ = ''
  if (!itemFilters.sortBy) {
    const savedSort = getFromLocalStorageByUserId('inventorySort', userId)
    sortBy_ = savedSort?.value || 'created_at.desc'
  }

  // Extract base params from itemFilters using constants
  const { baseParams: extractedBaseParams, filterParams: remainingFilters } = extractBaseParams(
    itemFilters,
    BASE_PARAM_KEYS
  )

  const baseParams = {
    page,
    perPage: perPage_,
    sortBy: extractedBaseParams.sortBy || sortBy_,
    ...extractedBaseParams,
  }

  // Combine remaining filters into a single comma-separated filter param
  const filterParams = Object.entries(remainingFilters).length
    ? `filter=${Object.entries(remainingFilters)
        .map(([key, value]) => `${key}=${value}`)
        .join(',')}`
    : ''

  const query = `${filterParams ? '?' + filterParams : ''}${
    Object.keys(baseParams).length ? (filterParams ? '&' : '?') + buildSearchParams({
      ...baseParams,
      ...(itemFilters.q ? { q: itemFilters.q } : {})
    }) : ''
  }`

  return { query, perPage_ }
}

/**
 * Attach a market-price recommendation to a single card by id.
 * `marketPrices` shape: `{ [cardId]: { suggested_price, last_updated } }`.
 * Returns a NEW card object with `price_recommendation` set when there's a match,
 * otherwise the card unchanged. Pure — safe for both list + detail surfaces.
 */
export const applyMarketPriceToCard = (card, marketPrices = {}) => {
  const recommendation = card && marketPrices?.[card.id]
  return recommendation
    ? { ...card, has_price_recommendation: true, price_recommendation: recommendation }
    : card
}

/**
 * Merge market-price recommendations into a list of inventory cards, keyed by id.
 * Returns a NEW array (immutable — keeps AG Grid's getRowId diffing happy).
 */
export const applyMarketPricesToCards = (cards = [], marketPrices = {}) => {
  if (!marketPrices || !Object.keys(marketPrices).length) return cards
  return cards.map((card) => applyMarketPriceToCard(card, marketPrices))
}